import React, { useState, useRef, lazy } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
const items = [{
  emoji: '🎯',
  title: 'I 6 Volumi del Percorso Completo (PDF)',
  desc: "Vol 1: Il Linguaggio Visivo. Vol 2-5: Storia dell'Arte (Dalla Preistoria al Contemporaneo). Vol 6: Tecniche Artistiche e Laboratori per la Secondaria.",
  color: 'bg-blue-500',
  shadow: 'shadow-[0_0_15px_rgba(59,130,246,0.5)]'
}, {
  emoji: '📋',
  title: "Il Registro dell'Esperto (PDF)",
  desc: 'La soluzione definitiva alla burocrazia: griglie di valutazione descrittiva (normativa italiana), verbali e programmazione annuale.',
  color: 'bg-green-500',
  shadow: 'shadow-[0_0_15px_rgba(34,197,94,0.5)]'
}, {
  emoji: '📱',
  title: 'Slide per la LIM: Capolavori a Confronto',
  desc: 'Lezioni digitali interattive per analizzare i grandi maestri (Leonardo, Michelangelo, Caravaggio, Banksy).',
  color: 'bg-purple-500',
  shadow: 'shadow-[0_0_15px_rgba(168,85,247,0.5)]'
}, {
  emoji: '🎨',
  title: 'Manuale di Inclusione BES/DSA',
  desc: 'Schede operative semplificate con font ad alta leggibilità per non lasciare indietro nessuno.',
  color: 'bg-orange-500',
  shadow: 'shadow-[0_0_15px_rgba(249,115,22,0.5)]'
}];
const carouselImages = [{
  src: 'https://cdn.magicpatterns.com/uploads/b8F9phEfa2U7Aeohef9QVp/Captura-de-pantalla-2026-05-05-a-la_s_-6.55.25_p._m..webp',
  alt: 'Scheda Operativa 01 — Il Punto'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/1vimWZnsu4uxXBGhGmtrCh/Captura-de-pantalla-2026-05-05-a-la_s_-6.55.52_p._m..webp',
  alt: 'La Forma — Figura e Sfondo'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/nqmpNr8b3MWY4V3cW6kXfM/Captura-de-pantalla-2026-05-05-a-la_s_-6.57.13_p._m..webp',
  alt: 'Dal Medioevo al Rinascimento — La Grande Trasformazione'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/5bjKCis2XZrT2CKVUojKkV/Captura-de-pantalla-2026-05-05-a-la_s_-6.57.23_p._m..webp',
  alt: 'Il Diario del Viaggiatore — Documento di Progetto'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/pfLHY1pUk4FPgPnrdsaVBd/Captura-de-pantalla-2026-05-05-a-la_s_-6.58.36_p._m..webp',
  alt: 'Scheda Operativa 05 — Il Neoclassicismo'
}];
export function ContentPreview() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const rafRef = useRef<number>(0);
  const handleScroll = () => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      if (!scrollRef.current) return;
      const container = scrollRef.current;
      const scrollLeft = container.scrollLeft;
      const clientWidth = container.clientWidth;
      const center = scrollLeft + clientWidth / 2;
      const children = container.children;
      let minDistance = Infinity;
      let active = 0;
      for (let i = 0; i < children.length; i++) {
        const child = children[i] as HTMLElement;
        const childCenter = child.offsetLeft + child.offsetWidth / 2;
        const distance = Math.abs(center - childCenter);
        if (distance < minDistance) {
          minDistance = distance;
          active = i;
        }
      }
      setActiveIndex(active);
    });
  };
  const scrollToIndex = (index: number) => {
    if (!scrollRef.current) return;
    const container = scrollRef.current;
    const child = container.children[index] as HTMLElement;
    if (!child) return;
    const scrollLeft = child.offsetLeft - container.clientWidth / 2 + child.clientWidth / 2;
    container.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    });
  };
  const scrollPrev = () => {
    if (activeIndex > 0) scrollToIndex(activeIndex - 1);
  };
  const scrollNext = () => {
    if (activeIndex < carouselImages.length - 1) scrollToIndex(activeIndex + 1);
  };
  return <section className="w-full bg-bg-dark py-20 px-4" data-id="element-23">
      <div className="max-w-5xl mx-auto" data-id="element-24">
        <div className="text-center mb-16" data-id="element-25">
          <h2 className="text-3xl sm:text-5xl font-black text-text-main mb-4 tracking-tight" data-id="element-26">
            Cosa include l'Archivio Didattico "Arte Pronta"?
          </h2>
          <p className="text-text-sec text-lg sm:text-xl max-w-2xl mx-auto" data-id="element-27">
            Strumenti digitali in PDF pronti da stampare o proiettare da oggi
            stesso.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-28">
          {items.map((item, i) => <div key={i} className="bg-bg-alt rounded-2xl border border-border-light overflow-hidden flex flex-col relative group hover:-translate-y-1 transition-transform duration-300" data-id="element-29">
              <div className={`${item.color} h-1.5 w-full ${item.shadow}`} data-id="element-30" />
              <div className="flex-1 flex flex-col p-8 relative" data-id="element-31">
                <div className={`absolute inset-0 ${item.color} opacity-[0.03] group-hover:opacity-[0.08] transition-opacity`} data-id="element-32"></div>

                <span className="text-5xl mb-5 block relative z-10 group-hover:scale-110 transition-transform duration-300" data-id="element-33">
                  {item.emoji}
                </span>
                <h3 className="text-xl sm:text-2xl font-bold text-text-main leading-snug relative z-10 mb-3" data-id="element-34">
                  {item.title}
                </h3>
                <p className="text-text-sec text-base leading-relaxed relative z-10" data-id="element-35">
                  {item.desc}
                </p>
              </div>
            </div>)}
        </div>

        <div className="mt-24 relative" data-id="element-36">
          <div className="text-center mb-10" data-id="element-37">
            <h3 className="text-2xl sm:text-3xl font-bold text-text-main inline-flex items-center gap-3" data-id="element-38">
              <span data-id="element-39">📸</span> Sfoglia il materiale all'interno
            </h3>
            <p className="text-text-sec text-sm mt-3 flex items-center justify-center gap-2" data-id="element-40">
              <span className="inline-flex items-center gap-1.5 sm:hidden" data-id="element-41">
                <ChevronLeft className="w-4 h-4 animate-pulse-composited" data-id="element-42" />
                Scorri per vedere di più
                <ChevronRight className="w-4 h-4 animate-pulse-composited" data-id="element-43" />
              </span>
              <span className="text-text-sec text-xs font-medium" data-id="element-44">
                {activeIndex + 1} / {carouselImages.length}
              </span>
            </p>
          </div>

          <div className="relative group max-w-[100vw] -mx-4 sm:mx-0" data-id="element-45">
            <button onClick={scrollPrev} className={`hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-bg-dark hover:bg-bg-alt border border-border-light p-3 rounded-full text-text-main transition-opacity ${activeIndex === 0 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Precedente" data-id="element-46">
              <ChevronLeft className="w-6 h-6" data-id="element-47" />
            </button>
            <button onClick={scrollNext} className={`hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-bg-dark hover:bg-bg-alt border border-border-light p-3 rounded-full text-text-main transition-opacity ${activeIndex === carouselImages.length - 1 ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-label="Successivo" data-id="element-48">
              <ChevronRight className="w-6 h-6" data-id="element-49" />
            </button>

            <div ref={scrollRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-4 sm:gap-6 px-[10vw] sm:px-[calc(50%-175px)] pb-8 pt-4" data-id="element-50">
              {carouselImages.map((img, i) => <div key={i} className={`snap-center shrink-0 w-[80vw] sm:w-[350px] bg-bg-alt border border-border-light rounded-2xl overflow-hidden p-2 sm:p-3 flex items-center justify-center shadow-[0_10px_30px_rgba(0,0,0,0.3)] transition-transform duration-300 ${activeIndex === i ? 'scale-100 opacity-100' : 'scale-95 opacity-50'}`} data-id="element-51">
                  <img src={img.src} alt={img.alt} loading="lazy" decoding="async" width={800} height={1131} className="w-full h-auto max-h-[400px] sm:max-h-[450px] object-contain rounded-xl bg-white/5" data-id="element-52" />
                </div>)}
            </div>
          </div>

          <div className="flex justify-center gap-0 mt-2" data-id="element-53">
            {carouselImages.map((_, i) => <button key={i} onClick={() => scrollToIndex(i)} className="flex items-center justify-center min-w-[48px] min-h-[48px] p-3" aria-label={`Vai all'immagine ${i + 1}`} data-id="element-54">
                <span className={`block h-2 rounded-full transition-all duration-300 ${i === activeIndex ? 'bg-primary w-8' : 'bg-border-light hover:bg-text-sec w-2'}`} data-id="element-55" />
              </button>)}
          </div>
        </div>
      </div>
    </section>;
}